import { supabase } from '../supabaseClient';
import { DEFAULT_BOWLERS_PER_LANE, DEFAULT_LANES_USED } from './signupOccupancy';
import {
  onlineSignupsEnabled,
  signupOpenByTournamentId,
} from './signupEnablement';

export { onlineSignupsEnabled, signupOpenByTournamentId };

const signupSettingsListeners = new Set();

export const subscribeSignupSettingsChanged = (listener) => {
  signupSettingsListeners.add(listener);
  return () => signupSettingsListeners.delete(listener);
};

export const notifySignupSettingsChanged = (tournamentId, settings) => {
  signupSettingsListeners.forEach((listener) => listener(tournamentId, settings));
};

export const AVERAGE_VERIFICATION_OFF = "off";
export const AVERAGE_VERIFICATION_OUT_OF_TOWN = "out_of_town";
export const AVERAGE_VERIFICATION_EVERYONE = "everyone";
export const DEFAULT_AVERAGE_PROOF_MESSAGE =
  "Upload a photo or PDF that shows these averages.";
export const DEFAULT_OUT_OF_TOWN_PROOF_MESSAGE =
  "Average verification is required for out-of-town bowlers. Upload a photo or PDF that shows these averages.";
export const AVERAGE_VERIFICATION_MISSING_DIRECTOR =
  "The director has not said which average to use. Contact them before you upload.";
export const AVERAGE_VERIFICATION_DIRECTOR_CONTACT =
  "Questions about these requirements: contact the tournament director.";
export const MAX_AVERAGE_VERIFICATION_MESSAGE = 500;

export const normalizeAverageVerification = (value) => {
  if (value === true || value === AVERAGE_VERIFICATION_EVERYONE) {
    return AVERAGE_VERIFICATION_EVERYONE;
  }
  if (value === AVERAGE_VERIFICATION_OUT_OF_TOWN) {
    return AVERAGE_VERIFICATION_OUT_OF_TOWN;
  }
  return AVERAGE_VERIFICATION_OFF;
};

export const directorAverageVerificationInstructions = (settings) =>
  String(settings?.averageVerificationMessage || "")
    .trim()
    .slice(0, MAX_AVERAGE_VERIFICATION_MESSAGE);

export const averageVerificationMessage = (settings) =>
  directorAverageVerificationInstructions(settings);

export const averageProofIsOffered = (settings) => {
  const mode = normalizeAverageVerification(
    settings?.averageVerification ?? settings?.requireAverageVerification
  );
  return (
    mode === AVERAGE_VERIFICATION_EVERYONE ||
    mode === AVERAGE_VERIFICATION_OUT_OF_TOWN
  );
};

export const averageVerificationInstructionsRequired = (settings) =>
  averageProofIsOffered(settings);

export const averageProofIsRequired = (settings) => {
  const mode = normalizeAverageVerification(
    settings?.averageVerification ?? settings?.requireAverageVerification
  );
  return mode === AVERAGE_VERIFICATION_EVERYONE;
};

export const getDefaultSignupSettings = (tournamentId) => ({
  tournamentID: tournamentId,
  allowSignups: true,
  lanesUsed: DEFAULT_LANES_USED,
  bowlersPerLane: DEFAULT_BOWLERS_PER_LANE,
  maxPerSquad: 4,
  requireEmail: false,
  requirePhone: false,
  requireUSBCID: true,
  requireAverage: true,
  averageVerification: AVERAGE_VERIFICATION_OFF,
  averageVerificationMessage: "",
  allowOnlineBrackets: false,
  paymentRequiredToHold: false,
  waitlistEnabled: true,
  closeAllHoursBeforeFirstSquad: null,
  closeHoursBeforeEachSquad: 48,
});

export const fetchTournamentSignupSettings = async (tournamentId) => {
  try {
    const { data, error } = await supabase
      .from("tournamentSignupSettings")
      .select("*")
      .eq("tournamentID", tournamentId);

    if (error) {
      console.error("Error fetching signup settings:", error);
    }

    const row = data?.[0] || {};
    const defaults = getDefaultSignupSettings(tournamentId);
    return {
      ...defaults,
      ...row,
      allowSignups: row.allowSignups !== false,
      waitlistEnabled: row.waitlistEnabled !== false,
      requireUSBCID: row.requireUSBCID !== false,
      requireAverage: row.requireAverage !== false,
      lanesUsed: row.lanesUsed ?? DEFAULT_LANES_USED,
      bowlersPerLane:
        Number(row.bowlersPerLane) || DEFAULT_BOWLERS_PER_LANE,
      averageVerification: normalizeAverageVerification(
        row.averageVerification ?? row.requireAverageVerification
      ),
      averageVerificationMessage: row.averageVerificationMessage || "",
    };
  } catch (error) {
    console.error("Error in fetchTournamentSignupSettings:", error);
    return getDefaultSignupSettings(tournamentId);
  }
};
